/**
 * The POS's palette, spacing and radii — the design tokens every shared POS view draws from.
 *
 * It lived in `components/CustomerSummary/` until 2026-09-09, by which point three unrelated
 * component folders were importing it through `../CustomerSummary/tokens`. A token file that
 * every surface reaches into is a package concern, not one component's: it moved here so a new
 * shared view imports `@rista/ui/library/theme/tokens` rather than borrowing a sibling's internals.
 *
 * 🔴 **Not the kiosk theme.** `packages/ui/kiosk/theme` serves merchant-configurable colour on
 * an unattended screen; this is the POS's own fixed palette, transcribed from
 * `components/CustomerSummary/style.scss` and `Containers/LoyaltyLeg/scss/style.scss` so the
 * uplifted views render the same as the web originals did. A shared token file rather than
 * per-component literals, because eight files draw the same card and the same divider.
 *
 * ⚠️ `--primary-color` was a CSS variable in the SCSS and has no cross-platform equivalent.
 * `PRIMARY` below is the value the POS resolves it to; a merchant-themed POS would thread it
 * through as a prop, which nothing does today.
 */
/**
 * @deprecated FROZEN (DESIGN_SYSTEM.md §7.1). New code draws roles from `useTheme()`; a surface leaves
 * `COLORS` when it moves onto roles, and a key is deleted when its last reader has. Its values never change
 * (live v1 surfaces read it) and it never grows: `theme/tokens.test.ts` pins its keys.
 */
export const COLORS = {
  /** Body text. */
  TEXT: "#111C36",
  /** Secondary text — was `opacity: .7` on TEXT in the SCSS. */
  TEXT_MUTED: "rgba(17, 28, 54, 0.7)",
  /** Tertiary text — was `opacity: .5`. */
  TEXT_FAINT: "rgba(17, 28, 54, 0.5)",
  /** The grey the empty states use. */
  TEXT_EMPTY: "#656565",
  /** Links and the active tab. */
  LINK: "#397EF5",
  PRIMARY: "#397EF5",
  /** Card and row borders. */
  BORDER: "#D9E3F1",
  /** The heavier divider between rows in a list. */
  BORDER_ROW: "#DEDEDF",
  /** Header/tab-strip rule. */
  BORDER_HEADER: "#E6ECF4",
  /** History-row border — a touch cooler than BORDER. */
  BORDER_HISTORY: "#E1E5EA",
  SURFACE: "#FFFFFF",
  /** The tinted panel behind the loyalty balance. */
  STANDING_BG: "#EEF2FF",
  STANDING_BORDER: "#DDE3F5",
  /** The circular close button's ground. */
  CHIP_BG: "#F4F6FA",
  /** Coupon-code chip. */
  COUPON_CHIP_BG: "#F1F1F1",
  COUPON_CHIP_BORDER: "#E1E2E2",
  /** Table header row. */
  TABLE_HEAD_BG: "#F7F7F7",
  TABLE_BORDER: "#D6D6D6",
  /** Rating bands — `tone` from `getRatingModel` maps onto these. */
  RATING_POOR: "#B91C1C",
  RATING_AVERAGE: "#ED951B",
  RATING_GOOD: "#52AF0F",
  RATING_NONE: "#26272C",
  /** Points earned / spent. */
  POINTS_EARNED: "#009A36",
  POINTS_DEDUCTED: "#D0765E",
  /**
   * Semantic accents — a confirmed state and a condition the user must act on.
   *
   * Named for what they MEAN rather than for the surface that first drew them: the loyalty
   * panel's "✓ Applied" chip and its "min. order value" line were the first two, and any later
   * view needing "this is done" or "this is not met yet" should reach for these rather than
   * re-picking a green and an amber.
   */
  SUCCESS: "#16A34A",
  SUCCESS_BG: "#DCFCE7",
  WARNING: "#D97706",
  /**
   * The tint behind an offer the user has not taken yet, and the accent that outlines it.
   *
   * ⚠️ Deliberately NOT `LINK`/`PRIMARY`. The POS's loyalty surfaces use a slightly deeper blue
   * (`#4980ED`) than its links (`#397EF5`), and both values are transcribed from the web
   * originals — collapsing them would visibly restyle one of the two.
   */
  OFFER_ACCENT: "#4980ED",
  OFFER_BG: "rgba(73, 128, 237, 0.05)",
  OFFER_BORDER: "rgba(73, 128, 237, 0.6)",
  /**
   * The POS's ACTION blue — the fill on a primary call to action.
   *
   * ⚠️ A third blue, and deliberately so. Both apps' `CtaComponent` paints its active state
   * `#0074FB` (`apps/web`'s `style.scss` `.active-cta`, `apps/mobile`'s `Colors.buttonPrimary`)
   * — the two agree exactly, and it is neither `LINK` (#397EF5, the POS's links and active tab)
   * nor `OFFER_ACCENT` (#4980ED, the redemption panel's own). `packages/ui` cannot import either
   * app's CTA, so a shared button that means "do the thing" reaches for this.
   */
  CTA: "#0074FB",
  /** An off switch's track — `apps/mobile`'s `inputBorderColor`, which its switch already used. */
  SWITCH_OFF: "#DADADA",
  /** "Leave program" — a destructive action, but not an alarming one. */
  DANGER: "#B64242",
  DANGER_BORDER: "rgba(17, 28, 54, 0.12)",
  /** The shaded ground behind an expired history row. */
  EXPIRED_BG: "rgba(225, 229, 234, 0.1)",
  /** Modal scrim. */
  SCRIM: "rgba(0, 0, 0, 0.4)",
  FAQ_QUESTION: "#2D3C32",
  FAQ_ANSWER: "#6A7282",
  /**
   * The dialog frames (`Sheet`, `Menu`), promoted from My Orders in L0 with their look kept: the
   * header's text and rule, the phone sheet's handle, and MUI's 50 % backdrop, which web v1's
   * dialogs draw (`SCRIM` above is the loyalty dialogs' lighter one).
   */
  SHEET_TITLE: "#131313",
  SHEET_SUBTITLE: "#696767",
  SHEET_RULE: "#F4F4F4",
  SHEET_HANDLE: "#D9D9D9",
  SHEET_SCRIM: "rgba(0, 0, 0, 0.5)",
  /** A shadow's colour; its spread is the member's own. */
  SHADOW: "#000000",
} as const;

// the static scales and the category letters moved to the palette (DESIGN_SYSTEM.md §2); kept here for importers
export { CATEGORY_LETTER_COLORS, RADIUS, SHADOWS, SPACING } from "./palette";

/** Maps a `RatingModel["tone"]` to its colour, so no view compares rating numbers. */
export const RATING_TONE_COLORS: Record<string, string> = {
  poor: COLORS.RATING_POOR,
  average: COLORS.RATING_AVERAGE,
  good: COLORS.RATING_GOOD,
  none: COLORS.RATING_NONE,
};
